import { useMutation } from '@tanstack/react-query';
import { SourcingStageStat, SourcingStageType } from 'api-clients/monolith';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { updateStageMappings } from '@/api-clients/update-stage-mappings';
import { useToaster } from '@/components/base/toaster';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { Typography } from '@/components/ui/typography';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { STAGE_TYPES } from '@/types/stage-types';

import { type Inputs, FormInputs } from './form-inputs';

interface StageMappingsFormProps {
  children: React.ReactNode;
  funnelData: SourcingStageStat[];
  showSpinner: (isSaving: boolean) => void;
  refetchFunnelData?: () => Promise<void>;
  hiringGoalConversionRate: number | null;
  setIsModalOpen: (isModalOpen: boolean) => void;
  setIsFormDirty: (isFormDirty: boolean) => void;
}

export function StageMappingsForm({
  children,
  funnelData,
  showSpinner,
  refetchFunnelData,
  hiringGoalConversionRate,
  setIsModalOpen,
  setIsFormDirty,
}: StageMappingsFormProps) {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const { notifySuccess, notifyError } = useToaster();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Set the form default values from the /sourcing_stage_stats API response
  const defaultValues = useMemo(() => {
    return funnelData.reduce(
      (acc, stage) => ({
        ...acc,
        [stage.stage_type]: {
          hireStageId: stage.hire_stage.id,
          expectedConversion: stage.conversion_stats.expected
            ? stage.conversion_stats.expected
            : null,
        },
      }),
      {
        landing: {
          expectedConversion: hiringGoalConversionRate,
        },
      } as Inputs,
    );
  }, [funnelData, hiringGoalConversionRate]);

  const hookForm = useForm<Inputs>({
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { dirtyFields },
    getValues,
  } = hookForm;
  const formValues = getValues();

  const { mutate: updateStageMappingsMutation, isPending } = useMutation({
    mutationFn: updateStageMappings,
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Stage mappings updated successfully!',
        }),
      });
      await refetchFunnelData?.();
      setIsModalOpen(false);
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update stage mappings!',
        }),
      });
    },
  });

  useEffect(() => {
    showSpinner(isPending);
  }, [isPending, showSpinner]);

  useEffect(() => {
    setIsFormDirty(Object.keys(dirtyFields).length > 0);
  }, [dirtyFields, setIsFormDirty, formValues]);

  const handleConfirm = () => {
    const changedFields = Object.entries(formValues)
      .filter(
        ([stageType]) =>
          stageType === 'hired' || Object.keys(dirtyFields).includes(stageType),
      )
      .map(([stageType, stageData]) => {
        return {
          stage_type: stageType as SourcingStageType,
          title: STAGE_TYPES[stageType as SourcingStageType],
          stage_id: stageData.hireStageId,
          expected_conversion: stageData.expectedConversion,
          is_automated: stageType === 'landing' || stageType === 'hired', // This is a temporary fix until we provide the option to the user to select if the stage is automated or not
        };
      });

    updateStageMappingsMutation({
      openingSlug,
      requestBody: {
        stages: changedFields,
      },
    });

    setConfirmOpen(false);
  };

  const onSubmit: SubmitHandler<Inputs> = () => {
    setConfirmOpen(true);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 p-6">
          <Typography variant="body1" className="!text-fx-neutral-600">
            <FormattedMessage defaultMessage="Select which workflow stages to track within your sourcing funnel. The predicted conversion is based on past data and shows the conversion percent from each stage to the “Hire” stage." />
          </Typography>

          <div className="grid grid-cols-[1fr_24px_2fr_1.1fr] items-center gap-6 px-4">
            <Typography variant="subtitle2">
              <FormattedMessage defaultMessage="Funnel stage" />
            </Typography>
            <span />
            <Typography variant="subtitle2">
              <FormattedMessage defaultMessage="Workflow stage" />
            </Typography>
            <Typography variant="subtitle2">
              <FormattedMessage defaultMessage="Predicted conversion" />
            </Typography>
          </div>

          {(Object.entries(STAGE_TYPES) as [SourcingStageType, string][]).map(
            ([stageType, stageName]) => (
              <FormInputs
                key={stageType}
                stageType={stageType}
                stageName={stageName}
                hookForm={hookForm}
              />
            ),
          )}
        </div>
        {children}
      </form>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={intl.formatMessage({
          defaultMessage: 'Confirm stage mappings update',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Updating stage mappings will update all funnels in the workflow',
        })}
        confirmLabel={intl.formatMessage({ defaultMessage: 'Confirm' })}
        cancelLabel={intl.formatMessage({ defaultMessage: 'Cancel' })}
        onConfirm={handleConfirm}
      />
    </>
  );
}
